import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { clientsApi } from '@/api/clients'
import { ApiError } from '@/api/errors'
import { invalidateClientViews } from '@/api/invalidate'
import { queryKeys } from '@/api/keys'
import type { Client, CreateClientPayload, UpdateClientPayload } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import { ClientFormDialog } from '@/components/clients/client-form-dialog'
import { ClientsTable } from '@/components/clients/clients-table'
import { ClientsToolbar } from '@/components/clients/clients-toolbar'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Pagination } from '@/components/pagination'
import { Button } from '@/components/ui/button'
import { flag, integer, oneOf, orElse, readSearch, text } from '@/lib/search-params'

const PAGE_SIZE = 25

const SHAPE = {
  q: text(),
  status: oneOf(['prospect', 'active', 'inactive'] as const),
  tag: text(),
  archived: flag(),
  sort: orElse(
    oneOf(['name', '-name', '-created_at', 'created_at', 'status', '-last_activity_at'] as const),
    'name' as const,
  ),
  page: orElse(integer({ min: 1 }), 1),
  /** Ouvre le dialogue de création — utilisé par la palette ⌘K. */
  nouveau: flag(),
}

export type ClientsSearch = ReturnType<typeof readClientsSearch>

function readClientsSearch(search: Record<string, unknown>) {
  return readSearch(SHAPE, search)
}

export const Route = createFileRoute('/_app/clients/')({
  validateSearch: readClientsSearch,
  component: ClientsPage,
})

function ClientsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()

  const [formOpen, setFormOpen] = useState(search.nouveau ?? false)
  const [editing, setEditing] = useState<Client | undefined>(undefined)
  const [toArchive, setToArchive] = useState<Client | null>(null)

  const params = {
    q: search.q ?? undefined,
    status: search.status,
    tag: search.tag ?? undefined,
    include_archived: search.archived ?? false,
    sort: search.sort,
    page: search.page,
    page_size: PAGE_SIZE,
  }

  const clients = useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: () => clientsApi.list(params),
    placeholderData: (previous) => previous,
  })

  const refresh = () => invalidateClientViews(queryClient)

  const save = useMutation({
    mutationFn: (payload: CreateClientPayload | UpdateClientPayload) =>
      editing
        ? clientsApi.update(editing.id, payload)
        : clientsApi.create(payload as CreateClientPayload),
    onSuccess: (client) => {
      void refresh()
      toast.success(editing ? `${client.name} mis à jour.` : `${client.name} créé.`)
      // Une création ouvre la fiche (#177) : la liste pagine à 25 et trie par
      // nom, le nouveau client pouvait tomber hors de la page sans que rien ne
      // dise où il était. La fiche est aussi la suite du geste — contacts,
      // adresse, projet.
      if (!editing) {
        void navigate({
          to: '/clients/$clientId',
          params: { clientId: client.id },
        })
      }
    },
  })

  const archive = useMutation({
    mutationFn: async (client: Client): Promise<void> => {
      if (client.archived_at) {
        await clientsApi.restore(client.id)
      } else {
        await clientsApi.archive(client.id)
      }
    },
    onSuccess: (_result, client) => {
      void refresh()
      toast.success(client.archived_at ? `${client.name} restauré.` : `${client.name} archivé.`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Opération impossible.')
    },
    onSettled: () => {
      setToArchive(null)
    },
  })

  const updateSearch = (next: Partial<ClientsSearch>) => {
    void navigate({
      search: (current) => ({ ...current, ...next, page: next.page ?? 1 }),
      replace: true,
    })
  }

  const openCreate = () => {
    setEditing(undefined)
    save.reset()
    setFormOpen(true)
  }

  // La palette ⌘K ouvre la création via `?nouveau=true` ; le paramètre est
  // consommé aussitôt pour ne pas rouvrir le dialogue au retour arrière.
  useEffect(() => {
    if (!search.nouveau) return
    openCreate()
    void navigate({ search: (current) => ({ ...current, nouveau: undefined }), replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- déclenché uniquement par le paramètre
  }, [search.nouveau])

  const openEdit = (client: Client) => {
    setEditing(client)
    save.reset()
    setFormOpen(true)
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Clients"
        actions={
          <Button onClick={openCreate}>
            <PlusIcon />
            Nouveau client
          </Button>
        }
      />

      <Frame>
        <ClientsToolbar search={search} onChange={updateSearch} />

        <ClientsTable
          clients={clients.data?.items ?? []}
          isPending={clients.isPending}
          error={clients.error}
          isAdmin={isAdmin}
          hasFilters={!!(search.q ?? search.status ?? search.tag)}
          onRetry={() => {
            void clients.refetch()
          }}
          onCreate={openCreate}
          onEdit={openEdit}
          onArchive={setToArchive}
        />

        {clients.data && clients.data.total > 0 && (
          <Pagination
            page={clients.data.page}
            pageSize={clients.data.page_size}
            total={clients.data.total}
            itemLabel="client"
            onPageChange={(page) => {
              updateSearch({ page })
            }}
          />
        )}

        <ClientFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          client={editing}
          pending={save.isPending}
          error={save.error}
          onSubmit={async (payload) => {
            try {
              await save.mutateAsync(payload)
              return true
            } catch {
              return false
            }
          }}
        />

        <ConfirmDialog
          open={toArchive !== null}
          title={toArchive?.archived_at ? 'Restaurer ce client ?' : 'Archiver ce client ?'}
          description={
            toArchive?.archived_at
              ? `${toArchive.name} redeviendra visible dans la liste et ses tâches réapparaîtront.`
              : toArchive
                ? `${toArchive.name} sera masqué de la liste. Ses tâches, notes et temps sont conservés et le client peut être restauré à tout moment.`
                : undefined
          }
          confirmLabel={toArchive?.archived_at ? 'Restaurer' : 'Archiver'}
          destructive={!toArchive?.archived_at}
          pending={archive.isPending}
          onOpenChange={(open) => {
            if (!open && !archive.isPending) setToArchive(null)
          }}
          onConfirm={() => {
            if (toArchive) archive.mutate(toArchive)
          }}
        />
      </Frame>
    </div>
  )
}
